import React, { useEffect, useRef, useState } from "react";
import {
  clearDeferredInstallPrompt,
  dismissInstallBannerPermanently,
  getDeferredInstallPrompt,
  getInAppCompactText,
  getInAppInstallSteps,
  getInstallAcceptedIntro,
  getInstallHint,
  getInstallPendingHint,
  getIosInstallBannerText,
  getIosInstallSteps,
  getIosInstallTroubleshootNote,
  getManualInstallIntro,
  getManualInstallStepsForCurrentBrowser,
  isInAppBrowser,
  isIos,
  isSafariBrowser,
  runInstallPromptSync,
  shouldShowInstallBanner,
  subscribeToInstallPrompt,
} from "../../../utils/installPrompt";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const ManualInstallSteps = ({
  intro,
  steps,
  onGotIt,
  gotItLabel = "Понятно",
  disabled = false,
  note,
}: {
  intro: string;
  steps: string[];
  onGotIt: () => void;
  gotItLabel?: string;
  disabled?: boolean;
  note?: string | null;
}) => (
  <>
    <p className="afisha-install-banner__hint">{intro}</p>
    <ol className="afisha-install-banner__steps">
      {steps.map((step) => (
        <li key={step}>{step}</li>
      ))}
    </ol>
    {note && (
      <p className="afisha-install-banner__hint afisha-install-banner__hint--note">
        {note}
      </p>
    )}
    <div className="afisha-install-banner__actions">
      <button
        type="button"
        className="afisha-install-banner__btn afisha-install-banner__btn--yes"
        onClick={onGotIt}
        disabled={disabled}
      >
        {gotItLabel}
      </button>
    </div>
  </>
);

const InAppBrowserBanner = ({
  onDismiss,
}: {
  onDismiss: (permanent: boolean) => void;
}) => {
  const [showInstructions, setShowInstructions] = useState(false);
  const icon = isIos() ? "icon-180.png" : "icon-192.png";

  if (showInstructions) {
    return (
      <div className="afisha-install-banner" role="region" aria-label="Инструкция">
        <img
          className="afisha-install-banner__logo"
          src={`${process.env.PUBLIC_URL}/icons/${icon}`}
          alt=""
          width={40}
          height={40}
        />
        <div className="afisha-install-banner__body">
          <ManualInstallSteps
            intro={getInAppCompactText()}
            steps={getInAppInstallSteps()}
            onGotIt={() => onDismiss(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="afisha-install-banner" role="region" aria-label="Открыть в браузере">
      <img
        className="afisha-install-banner__logo"
        src={`${process.env.PUBLIC_URL}/icons/${icon}`}
        alt=""
        width={40}
        height={40}
      />
      <div className="afisha-install-banner__body">
        <p className="afisha-install-banner__text afisha-install-banner__text--compact">
          {getInAppCompactText()}
        </p>
        <div className="afisha-install-banner__actions">
          <button
            type="button"
            className="afisha-install-banner__btn afisha-install-banner__btn--no"
            onClick={() => onDismiss(true)}
          >
            Не сейчас
          </button>
          <button
            type="button"
            className="afisha-install-banner__btn afisha-install-banner__btn--yes"
            onClick={() => setShowInstructions(true)}
          >
            Инструкция
          </button>
        </div>
      </div>
    </div>
  );
};

const IosInstallBanner = ({
  onDismiss,
}: {
  onDismiss: (permanent: boolean) => void;
}) => {
  const [showInstructions, setShowInstructions] = useState(false);

  if (showInstructions) {
    return (
      <div className="afisha-install-banner" role="region" aria-label="Инструкция">
        <img
          className="afisha-install-banner__logo"
          src={`${process.env.PUBLIC_URL}/icons/icon-180.png`}
          alt=""
          width={40}
          height={40}
        />
        <div className="afisha-install-banner__body">
          <ManualInstallSteps
            intro={
              isSafariBrowser()
                ? "Добавьте КудаВГород на главный экран:"
                : getIosInstallBannerText()
            }
            steps={getIosInstallSteps()}
            note={getIosInstallTroubleshootNote()}
            onGotIt={() => onDismiss(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="afisha-install-banner" role="region" aria-label="Добавление на главный экран">
      <img
        className="afisha-install-banner__logo"
        src={`${process.env.PUBLIC_URL}/icons/icon-180.png`}
        alt=""
        width={40}
        height={40}
      />
      <div className="afisha-install-banner__body">
        <p className="afisha-install-banner__text afisha-install-banner__text--compact">
          Добавить на главный экран
        </p>
        <div className="afisha-install-banner__actions">
          <button
            type="button"
            className="afisha-install-banner__btn afisha-install-banner__btn--no"
            onClick={() => onDismiss(true)}
          >
            Не сейчас
          </button>
          <button
            type="button"
            className="afisha-install-banner__btn afisha-install-banner__btn--yes"
            onClick={() => setShowInstructions(true)}
          >
            Инструкция
          </button>
        </div>
      </div>
    </div>
  );
};

const AndroidInstallBanner = ({
  onDismiss,
}: {
  onDismiss: (permanent: boolean) => void;
}) => {
  const [hintMode, setHintMode] = useState<"none" | "pending" | "manual">("none");
  const [manualIntro, setManualIntro] = useState("");
  const [installing, setInstalling] = useState(false);
  const installPromptRef = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    return subscribeToInstallPrompt((event) => {
      installPromptRef.current = event;
    });
  }, []);

  const showManualSteps = (intro: string) => {
    setManualIntro(intro);
    setHintMode("manual");
  };

  const handleYes = () => {
    const installPrompt =
      installPromptRef.current ?? getDeferredInstallPrompt();
    if (!installPrompt) {
      showManualSteps(getInstallHint());
      return;
    }

    setInstalling(true);
    setHintMode("pending");
    setManualIntro(getInstallPendingHint());

    runInstallPromptSync(installPrompt, (accepted) => {
      setInstalling(false);
      installPromptRef.current = null;
      clearDeferredInstallPrompt();

      if (accepted) {
        showManualSteps(getInstallAcceptedIntro());
        return;
      }

      showManualSteps(getManualInstallIntro());
    });
  };

  return (
    <div className="afisha-install-banner" role="region" aria-label="Установка ярлыка">
      <img
        className="afisha-install-banner__logo"
        src={`${process.env.PUBLIC_URL}/icons/icon-192.png`}
        alt=""
        width={40}
        height={40}
      />
      <div className="afisha-install-banner__body">
        {hintMode === "none" ? (
          <>
            <p className="afisha-install-banner__text">
              Установить приложение на телефон?
            </p>
            <div className="afisha-install-banner__actions">
              <button
                type="button"
                className="afisha-install-banner__btn afisha-install-banner__btn--yes"
                onClick={handleYes}
              >
                Да
              </button>
              <button
                type="button"
                className="afisha-install-banner__btn afisha-install-banner__btn--no"
                onClick={() => onDismiss(true)}
                disabled={installing}
              >
                Нет
              </button>
            </div>
          </>
        ) : hintMode === "pending" ? (
          <>
            <p className="afisha-install-banner__hint">{manualIntro}</p>
            <div className="afisha-install-banner__actions">
              <button
                type="button"
                className="afisha-install-banner__btn afisha-install-banner__btn--yes"
                disabled
              >
                Устанавливаем…
              </button>
            </div>
          </>
        ) : (
          <ManualInstallSteps
            intro={manualIntro}
            steps={getManualInstallStepsForCurrentBrowser()}
            onGotIt={() => onDismiss(false)}
            disabled={installing}
          />
        )}
      </div>
    </div>
  );
};

export const InstallShortcutBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!shouldShowInstallBanner()) return;
    setVisible(true);
  }, []);

  if (!visible) return null;

  const onDismiss = (permanent: boolean) => {
    if (permanent) {
      dismissInstallBannerPermanently();
    }
    setVisible(false);
  };

  if (isInAppBrowser()) {
    return <InAppBrowserBanner onDismiss={onDismiss} />;
  }

  if (isIos()) {
    return <IosInstallBanner onDismiss={onDismiss} />;
  }

  return <AndroidInstallBanner onDismiss={onDismiss} />;
};
